// this module assumes a number of fake users and no authentication
// for demo use only

import * as userApi from 'services/user'

import Yggdrasil from 'services/yggdrasil'
import { localStorageKeys } from 'store/plugins'
import accessHelper from 'helpers/accessHelper'

const state = {
    currentUser: JSON.parse(window.localStorage.getItem('currentUser') || null),
    userInfo: {},
    orderHistory: [],
    currentOrder: [],
    watchLists: [],
    reportingItems: [],
    isReportingAllowed: false,
    isLoginBoxOpen: false,
    recommendedCourses: [],
    selectedSummaryGraphYear: null,
    packageInfo: [],
    reportingCategories: [],
    reportingHideCoursesInProgress: false,
    reportingHideCompletedCourses: false,
    digitalClassroomSubscriptions: [],
    sessionExpiration: JSON.parse(window.localStorage.getItem(localStorageKeys.sessionExpiration) || null),
    reportingExcelLink: null,
    reportingSubjectAreas: [],
    showFooterSubscribe: false,
    allDataLoaded: false,
    // reportingConfiguration: {}
}

const mutations = {
    SET_CURRENT_USER (state, user) {
        state.currentUser = user
    },
    ASSIGN_USER_INFO (state, list) {
        state.userInfo = list
    },
    ASSIGN_PACKAGE_INFO (state, list) {
        state.packageInfo = list
    },
    UPDATE_USER_INFO (state, modifiedUser) {
        state.userInfo = modifiedUser
    },
    CLEAR_USER_INFO (state) {
        state.userInfo = {}
    },
    LOGOUT (state) {
        state.currentUser = null
    },
    ASSIGN_ALL_DATA_LOADED (state, isLoaded) {
        state.allDataLoaded = isLoaded
    },
    ASSIGN_ORDER_HISTORY (state, list) {
        state.orderHistory = list
    },
    ASSIGN_CURRENT_ORDER (state, list) {
        state.currentOrder = list
    },
    ASSIGN_REPORTING_CATEGORIES (state, items) {
        state.reportingCategories = items
    },
    ASSIGN_REPORTING_PRIVILEDGE (state, isAllowed) {
        state.isReportingAllowed = isAllowed
    },
    TOGGLE_LOGIN_BOX (state) {
        state.isLoginBoxOpen = !state.isLoginBoxOpen
    },
    SET_LOGIN_BOX (state, isOpen) {
        if (isOpen) {
            return false
        }
        state.isLoginBoxOpen = isOpen
    },
    ASSIGN_RECOMMENDED_COURSES_LIST (state, list) {
        state.recommendedCourses = list
    },
    SET_SUMMARY_GRAPH_YEAR (state, option) {
        state.selectedSummaryGraphYear = option
    },
    ASSIGN_REPORTING_TOGGLE_COURSES_IN_PROGRESS (state, value) {
        state.reportingHideCoursesInProgress = value
    },
    ASSIGN_REPORTING_TOGGLE_COMPLETED_COURSES (state, value) {
        state.reportingHideCompletedCourses = value
    },
    ASSIGN_DIGITAL_CLASSROOM_SUBSCRIPTIONS (state, subscriptions) {
        state.digitalClassroomSubscriptions = subscriptions
    },
    SET_SESSION_EXPIRATION (state, expirationTimestamp) {
        state.sessionExpiration = expirationTimestamp
    },
    ASSIGN_REPORTING_EXCEL_LINK (state, excelLink) {
        state.reportingExcelLink = excelLink
    },
    ASSIGN_REPORTING_SUBJECT_AREAS (state, subjectAreas) {
        state.reportingSubjectAreas = subjectAreas
    },
    SET_SHOW_FOOTER_SUBSCRIBE (state, value) {
        state.showFooterSubscribe = value
    }
}

const actions = {
    setSummaryGraphYear (store, option) {
        store.commit('SET_SUMMARY_GRAPH_YEAR', option)
    },
    setCurrentUser (store, user) {
        store.commit('SET_CURRENT_USER', user)
    },
    setDataLoaded (store, isLoaded) {
        store.commit('ASSIGN_ALL_DATA_LOADED', isLoaded)
    },
    async loadUserInfo (store) {
        let userInfo = await Yggdrasil.loadUserInfo()
        const detailedUserInfo = await Yggdrasil.loadDetailedUserInfoWithPermissions()
        userInfo = { ...detailedUserInfo, ...userInfo }
        store.commit('ASSIGN_USER_INFO', userInfo)
        let isReportingAllowed = null
        if (userInfo.allowSupervisorReport || userInfo.reportLevel !== null) {
            isReportingAllowed = true
        } else {
            isReportingAllowed = false
        }
        store.commit('ASSIGN_REPORTING_PRIVILEDGE', isReportingAllowed)

        if (store.getters.getUserInfo.allowMarketingCommunication) {
            let _hsq = window._hsq = window._hsq || []
            _hsq.push(['identify', { email: store.getters.getUserInfo.email }])
            _hsq.push(['trackPageView'])
        }
    },
    async loadPackageInfo (store) {
        const packageInfo = await Yggdrasil.loadPackageInfo()
        store.commit('ASSIGN_PACKAGE_INFO', packageInfo)
    },
    async updateUserInfo (store, newUserInfo) {
        const modifiedUser = await Yggdrasil.updateUserInfo(newUserInfo)
        store.commit('UPDATE_USER_INFO', modifiedUser)
    },
    async changePasswordFromUserInfo (store, passwordInfo) {
        return Yggdrasil.changePasswordFromUserInfo(passwordInfo)
    },
    async clearUserData (store) {
        store.commit('LOGOUT')
        store.commit('CLEAR_USER_INFO')
        store.commit('ASSIGN_ORDER_HISTORY', [])
        store.commit('SET_SESSION_EXPIRATION', null)
    },
    async loadOrderHistory (store) {
        const list = await userApi.getOrderHistory()
        store.commit('ASSIGN_ORDER_HISTORY', list)
    },
    async loadCurrentOrder (store) {
        const list = await userApi.getCurrentOrder()
        store.commit('ASSIGN_CURRENT_ORDER', list)
    },
    async joinByToken (store, token) {
        return Yggdrasil.joinByToken(token)
    },
    async loadJobTitles (store) {
        let jobs = await Yggdrasil.loadJobTitles()
        return jobs.map(job => ({ id: job.id, label: job.value }))
    },
    async loadAgreementForm (store, slug) {
        return Yggdrasil.loadAgreementForm(slug)
    },
    async loadFreeSubscription (store, slug) {
        return Yggdrasil.loadFreeSubscription(slug)
    },
    async sendTrial (store, { slug, trialForm }) {
        return Yggdrasil.sendTrial(slug, trialForm)
    },
    async sendFreeSubscription (store, { slug, freeSubscription }) {
        return Yggdrasil.sendFreeSubscription(slug, freeSubscription)
    },
    async sendFreeSubscriptionRequestForExisting (store, { slug }) {
        return Yggdrasil.sendFreeSubscriptionRequestForExisting(slug)
    },
    async sendAgreementRequestForNew (store, { slug, agreementForm }) {
        return Yggdrasil.sendAgreementRequestForNew(slug, agreementForm)
    },
    async sendAgreementRequestForExisting (store, { slug }) {
        return Yggdrasil.sendAgreementRequestForExisting(slug)
    },
    toggleLoginBox (store) {
        store.commit('TOGGLE_LOGIN_BOX')
    },
    setLoginBox (store, isOpen) {
        store.commit('SET_LOGIN_BOX', isOpen)
    },
    async loadRecommendedCourses (store) {
        const recommendedCourses = await Yggdrasil.loadRecommendedCourses()
        store.commit('ASSIGN_RECOMMENDED_COURSES_LIST', recommendedCourses)
    },
    async hideOrRestoreUncompletedCourseFromList (store, { courseId, isHidden }) {
        return Yggdrasil.hideOrRestoreUncompletedCourseFromList({ courseId, isHidden })
    },
    reportingToggleCoursesInProgress (store, value) {
        store.commit('ASSIGN_REPORTING_TOGGLE_COURSES_IN_PROGRESS', value)
    },
    reportingToggleCompletedCourses (store, value) {
        store.commit('ASSIGN_REPORTING_TOGGLE_COMPLETED_COURSES', value)
    },
    setMetadata (store, { key, value }) {
        return Yggdrasil.setUserMetadata(key, value)
    },
    setSessionExpiration (store, expirationTimestamp) {
        store.commit('SET_SESSION_EXPIRATION', expirationTimestamp)
    },
    setChatTokenExpiration (store, expirationTimestamp) {
        store.commit('SET_CHAT_TOKEN_EXPIRATION', expirationTimestamp)
    },
    showFooterSubscribe (store, value) {
        store.commit('SET_SHOW_FOOTER_SUBSCRIBE', value)
    }
}

const getters = {
    getCurrentOrder: (state) => state.currentOrder,
    getCurrentUser: (state) => state.currentUser,
    getDigitalClassroomSubscriptions: ({ digitalClassroomSubscriptions }) => digitalClassroomSubscriptions,
    getGraphYear: (state) => state.selectedSummaryGraphYear,
    getIsLoginBoxOpen: (state) => state.isLoginBoxOpen,
    getIsReportingAllowed: (state) => state.isReportingAllowed,
    getOrderHistory: (state) => state.orderHistory,
    getPackageInfo: (state) => state.packageInfo,
    getRecommendedCourses: (state) => state.recommendedCourses,
    getReportingCategories: (state) => state.reportingCategories,
    getReportingExcelLink: (state) => state.reportingExcelLink,
    getReportingHideCompletedCourses: (state) => state.reportingHideCompletedCourses,
    getReportingHideCoursesInProgress: (state) => state.reportingHideCoursesInProgress,
    getReportingItems: (state) => state.reportingItems.members,
    getReportingSubjectAreas: (state) => state.reportingSubjectAreas,
    getSessionExpiration: ({ sessionExpiration }) => sessionExpiration,
    getShowFooterSubscribe: (state) => state.showFooterSubscribe,
    getUserAccessablePackages: (state) => state.packageInfo.filter(packageItem => packageItem.accessLevel >= accessHelper.ACCESS_LEVEL_FULL),
    getUserInfo: (state) => state.userInfo,
    getWatchLists: (state) => state.watchLists,
    isAllDataLoaded: (state) => state.allDataLoaded,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
